import { ChangeDetectorRef, Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IFormInputMask } from '@shared/core';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NgxMaskConfig, NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { IFormFieldInputConfig } from '../../models';
import { BaseFormFieldComponent, baseProviderComponent } from '../base-form-field.component';
import { NgIf } from '@angular/common';
import { LocalizationPipe } from '@shared/modules/localization';
import { NzIconModule } from 'ng-zorro-antd/icon';

const maskBaseConfig: Partial<NgxMaskConfig> = {
  clearIfNotMatch: false,
};

@Component({
  selector: 'ui-form-input',
  templateUrl: './form-input.component.html',
  styleUrl: './form-input.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgIf,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    FormsModule,
    NgxMaskDirective,
    LocalizationPipe,
  ],
  providers: [...baseProviderComponent(UIFormInput), provideNgxMask(maskBaseConfig)],
})
export class UIFormInput extends BaseFormFieldComponent {
  @Input() override value?: string = '';
  @Input() isGrouped = false;
  @Input() override set config(configValue: IFormFieldInputConfig) {
    this.label = configValue.label;
    this.placeholder = configValue.placeholder ?? '';
    this.maskConfig = configValue.maskConfig;
    this.suffixIcon = configValue.suffixIcon;
    this.hideLabel = configValue.hideLabel ?? false;
    this.hideError = configValue.hideError ?? false;
    this.isGrouped = configValue.isGrouped ?? false;
    this.isUpperCase = configValue.isUpperCase ?? false;
  }

  @Input() maskConfig?: IFormInputMask;
  @Input() suffixIcon?: string;

  @Input() size: 'large' | 'medium' | 'small' = 'medium'; // Default size

  private cdr = inject(ChangeDetectorRef);

  override get errorTip(): string {
    // Check if there are any errors, return early if none.
    if (!this.control?.errors) {
      return '';
    }

    // Directly access the first error key and value.
    const [firstErrorKey, firstErrorValue] = Object.entries(this.control.errors)[0];

    // Check for 'mask' error specifically.
    if (firstErrorKey === 'mask') {
      // Return the mask error text if it exists, otherwise use a default message.
      return this.maskConfig?.errorText || 'form.error.invalidData';
    }

    // If the error value is a string, return it, otherwise use a default message.
    return typeof firstErrorValue === 'string'
      ? firstErrorValue
      : typeof firstErrorValue === 'object'
      ? firstErrorValue.translation
      : 'form.error.required';
  }

  onBlur(): void {
    this.onTouched();
  }

  // ControlValueAccessor methods
  override writeValue(value: string): void {
    this.value = value;
    this.updateControlValidity(); // Ensure control state is updated
    this.cdr.detectChanges();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let text = target.value;
    if (this.maskConfig?.formatter) {
      text = this.maskConfig.formatter(text);
    }
    this.value = this.isUpperCase ? text.toUpperCase() : text;
    this.valueChange.emit(this.value);
    this.onChange(this.value);
    this.updateControlValidity(); // Ensure control state is updated
  }

  onClear(): void {
    this.value = '';
    this.valueChange.emit(this.value);
    this.onChange(this.value);
    this.updateControlValidity(); // Ensure control state is updated
    this.onEnterPressed();
  }
}
